<script setup>
    import { Swiper, SwiperSlide } from 'swiper/vue';
    import { EffectFade, Navigation, Pagination, A11y, Thumbs, FreeMode } from 'swiper';
    import 'swiper/css';
    import 'swiper/css/navigation';
    import 'swiper/css/pagination';
    import 'swiper/css/effect-fade';
    import {onMounted, ref} from "vue";
    import {useVideoModalControlsStore} from "../stores/VideoModalControls";
    import {storeToRefs} from "pinia";

    const props = defineProps({
        testimonials: {
            type: Object,
            required: true
        },

        ctaLink: {
            type: String,
            required: false
        },

        ctaText: {
            type: String,
            required: false
        },

        imageData: {
            type: Array,
            required: false,
            default: () => { return [] }
        }
    })
    const modules = [EffectFade, Navigation, Pagination, A11y, Thumbs, FreeMode]

    const thumbsSwiper = ref(null),
          activeIndex = ref(0),
          progress = ref(null),
          speed = ref(300)

    const videoModalControls = useVideoModalControlsStore(),
        { modalOpen, modalSetTitle } = videoModalControls

    function populateModal(url, title) {
        modalOpen(url)
        modalSetTitle(title)
    }

    onMounted(() => {
        progress.value.style.maxWidth = progressWidth() + '%'

        if (window?.reduced_motion) {
            speed.value = 0;
        }
    })

    function progressWidth() {
        return ((100 / props.testimonials.length) * (activeIndex.value + 1))
    }

    function onSlideChange(swiper) {
        activeIndex.value = swiper.activeIndex
        progress.value.style.maxWidth = progressWidth() + '%'

        if (window.innerWidth < 1200) {
            swiper.snapGrid = [...swiper.slidesGrid]
            thumbsSwiper.value.slideTo(swiper.activeIndex)
        }
    }

    const setThumbsSwiper = (swiper) => {
        thumbsSwiper.value = swiper
    }
</script>

<template>
    <div class="">
        <div class="md:grid md:grid-cols-2 w-full relative">
            <div class="z-10 w-full relative">
                <div class="block-inner flex justify-between items-center z-1 py-20">
                    <div class="relative w-full md:w-[calc(100%_-_200px)]" aria-hidden="true">
                        <div ref="progress" class="w-full h-1 bg-cintra-orange transition-all duration-300 relative z-1"  />
                        <div class="h-px bg-cintra-grey-light left-0 right-0 absolute top-1/2 -translate-y-1/2" />
                    </div>
                    <div class="text-3xl md:text-4xl w-24 h-12 flex items-center justify-between">
                        <button class="cintra-testimonial-slider-prev w-12 h-12 relative transition duration-300 dark:text-white hover:text-cintra-orange">
                            <i class="fa-light fa-circle-arrow-left absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2"></i>
                        </button>
                        <button class="cintra-testimonial-slider-next w-12 h-12 relative transition duration-300 dark:text-white hover:text-cintra-orange">
                            <i class="fa-light fa-circle-arrow-right absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2"></i>
                        </button>
                    </div>
                </div>
                <swiper
                    :modules="modules"
                    :slides-per-view="1"
                    :loop="false"
                    :speed="speed"
                    :navigation="{
                        nextEl: '.cintra-testimonial-slider-next',
                        prevEl: '.cintra-testimonial-slider-prev'
                    }"
                    :effect="'fade'"
                    :fadeEffect="{ crossFade: true }"
                    :thumbs="{ swiper: thumbsSwiper }"
                    class="w-full"
                    @slideChange="onSlideChange"
                >
                    <swiper-slide v-for="(testimonial, index) in props.testimonials">
                        <div class="block-inner w-full" :key="index">
                            <div class="text w-full text-cintra-grey flex flex-col justify-center">
                                <div>
                                    <transition name="quote" v-show="activeIndex === index">
                                        <blockquote v-html="testimonial.quote"></blockquote>
                                    </transition>
                                    <transition name="author" v-show="activeIndex === index">
                                        <p class="px-10 md:px-12">
                                            {{ testimonial.author_name }}{{ testimonial.author_company_name ? `, ${testimonial.author_company_name}` : '' }}
                                        </p>
                                    </transition>
                                </div>
                            </div>
                        </div>
                    </swiper-slide>
                </swiper>
                <div class="block-inner relative z-1 px-10 md:px-12 pt-2 pb-20">
                    <a v-if="props.ctaLink && props.ctaText" class="theme-button" :href="props.ctaLink">{{ props.ctaText }}</a>
                </div>
            </div>
            <div class="w-full z-10">
                <swiper
                    :modules="modules"
                    :slides-per-view="1"
                    :loop="false"
                    :speed="speed"
                    :watchSlidesProgress="true"
                    :free-mode="false"
                    :allow-touch-move="false"
                    class="w-full h-full aspect-4/3 md:aspect-auto"
                    @slideChange="onSlideChange"
                    @swiper="setThumbsSwiper"
                >
                    <swiper-slide v-for="(testimonial, index) in props.testimonials">
                        <div class="group flex flex-col justify-center items-center h-full relative" :key="index">
                            <button
                                v-if="testimonial.video_testimonial && testimonial.video_embed_link"
                                type="button"
                                aria-label="Click to watch a video about this testimonial"
                                class="absolute cursor-pointer top-1/2 left-1/2 md:left-2/3 -translate-y-1/2 -translate-x-1/2 w-20 h-20 rounded-full overflow-hidden appearance-none z-1"
                                @click="populateModal(testimonial.video_embed_link, testimonial.title)"
                            >
                                <div class="w-full h-full bg-white opacity-50 transition duration-300 group-hover:opacity-80 group-hover:drop-shadow-md" />
                                <div class="absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/3 border-t-[10px] border-t-transparent border-b-[10px] border-b-transparent border-l-[20px] border-l-cintra-grey h-0 w-0" />
                            </button>
                            <picture v-if="imageData?.[index]?.featured_image">
                                <source
                                    :srcset="imageData[index].featured_image?.webp"
                                    :width="imageData[index].featured_image.width"
                                    :height="imageData[index].featured_image.height"
                                    :alt="testimonial?.featured_image?.alt"
                                    type="image/webp"
                                    class="w-full h-full absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 object-cover -z-1"
                                >
                                <img
                                    :src="imageData[index].featured_image?.default"
                                    :width="imageData[index].featured_image.width"
                                    :height="imageData[index].featured_image.height"
                                    :alt="testimonial?.featured_image?.alt"
                                    class="w-full h-full absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 object-cover -z-1"
                                >
                            </picture>
                            <img
                                v-else-if="testimonial?.featured_image?.permalink"
                                :src="testimonial.featured_image.permalink"
                                :alt="testimonial.featured_image?.alt"
                                loading="lazy"
                                class="w-full h-full absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 object-cover -z-1"
                            >
                        </div>
                    </swiper-slide>
                </swiper>
            </div>
        </div>
    </div>
</template>
<style lang="scss">
    .swiper-button-disabled {
        @apply pointer-events-none;
        @apply opacity-30;
    }

    .quote-enter-active,
    .quote-leave-active {
        @apply transition;
        @apply duration-500;
    }
    .quote-enter-from,
    .quote-enter,
    .quote-leave-to {
        @apply motion-safe:opacity-0;
        @apply motion-safe:translate-y-1/3;
    }
    .quote-enter-to {
        @apply opacity-100;
        @apply translate-y-0;
    }

    .author-enter-active,
    .author-leave-active {
        @apply transition;
        @apply duration-500;
        @apply delay-300;
    }
    .author-enter-from,
    .author-enter,
    .author-leave-to {
        @apply motion-safe:opacity-0;
        @apply motion-safe:translate-y-6;
    }
    .author-enter-to {
        @apply opacity-100;
        @apply translate-y-0;
    }
</style>
